import styles from "./styles.module.scss";
import {
  Button,
  CustomPhoneInput,
  CustomSelect,
  Input,
  OptionType,
  optionTypeSchema,
} from "components";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { useForm, SubmitHandler } from "react-hook-form";
import { useEffect, useState } from "react";
import { addCandidateAssessmentRequestData } from "api";

interface AbiaEssentialInfoProps {
  candidate:
    | {
        firstName: string;
        lastName: string;
        email: string;
        dob: string;
        gender: string;
        phone: string;
        assessmentStatus: string;
      }
    | undefined;
  assessment: {
    title: string;
    id: string;
    _id: string;
    employer: { name: string; logo: string };
  };
  handleCheck: (email: string) => void;
  handleReset: () => void;
  showDetails: boolean;
  handleAddCandidate: (data: addCandidateAssessmentRequestData) => void;
}

const AbiaCheckRegistrationUI: React.FC<AbiaEssentialInfoProps> = ({
  candidate,
  assessment,
  handleCheck,
  handleReset,
  showDetails,
  handleAddCandidate,
}) => {
  const [clear, setClear] = useState(false);
  return (
    <section className={styles.modal}>
      <section className={styles.assessmentInfo}>
        <img
          src={assessment?.employer.logo}
          alt={`${assessment?.employer.name} logo`}
        />
        <h1 className={styles.ttl}>{assessment?.title}</h1>
      </section>
      <CheckRegistrationForm submit={handleCheck} clear={clear} />
      {candidate && showDetails ? (
        <section>
          <h3 className={styles.ttl3}>Candidate Registration Details</h3>
          <div className={styles.info}>
            <div className={styles.info__item}>
              <p>First Name:</p>
              <p>{candidate.firstName}</p>
            </div>
            <div className={styles.info__item}>
              <p>Last Name:</p>
              <p>{candidate.lastName}</p>
            </div>
            <div className={styles.info__item}>
              <p>Email:</p>
              <p>{candidate.email}</p>
            </div>
            <div className={styles.info__item}>
              <p>Phone number:</p>
              <p>{candidate.phone}</p>
            </div>
            <div className={styles.info__item}>
              <p>Gender:</p>
              <p>{candidate.gender}</p>
            </div>
            <div className={styles.info__item}>
              <p>Date of birth:</p>
              <p>{candidate.dob}</p>
            </div>
            <div className={styles.info__item}>
              <p> Status:</p>
              <p style={{textTransform: "capitalize"}} >Assessment {candidate.assessmentStatus}</p>
              </div>
          </div>
          <Button
            className={styles.btn}
            type={"outline"}
            onClick={() => {
              handleReset();
              setClear((prev) => !prev);
            }}
          >
            Reset
          </Button>
        </section>
      ) : null}
      <hr style={{ marginBottom: "4rem" }} />
      <NewRegistrationForm
        submit={handleAddCandidate}
        assessmentId={assessment._id}
      />
    </section>
  );
};

export { AbiaCheckRegistrationUI };

// CheckRegistrationForm component
interface StartData {
  email: string;
}

const initData: StartData = {
  email: "",
};

const checkSchema = yup
  .object()
  .shape({
    email: yup.string().required("Required").email("Enter a valid email"),
  })
  .required();

interface StartAssessmentProps {
  submit: (email: string) => void;
  clear: boolean;
}

// CheckRegistrationForm component
const CheckRegistrationForm: React.FC<StartAssessmentProps> = ({
  submit,
  clear,
}) => {
  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
    reset,
  } = useForm<StartData>({
    resolver: yupResolver(checkSchema),
    defaultValues: initData,
  });

  useEffect(() => {
    reset(initData);
  }, [clear]);

  const onSubmit: SubmitHandler<StartData> = (data) => submit(data.email);

  return (
    <>
      <section className={styles.checkFormRoot}>
        <h2 className={styles.ttl2}>Check Candidate Registration</h2>
        <p className={styles.txt}>
          Enter the candidate's email address to check their registration status
          for the assessment. Please ensure the email is correct.
        </p>
        <form>
          <Input
            label="Email address"
            placeholder="e.g. johndoe@gmail.com"
            type="email"
            required
            validatorMessage={errors.email?.message}
            name="email"
            register={register}
            value={watch("email")}
            showRequired
            parentClassName={styles.inputWrap}
          />
          <Button
            className={styles.btn}
            type={"fill"}
            onClick={handleSubmit(onSubmit)}
          >
            Check candidate
          </Button>
        </form>
      </section>
    </>
  );
};

interface NewRegistrationFormData {
  email: string;
  first_name: string;
  last_name: string;
  gender: OptionType;
  number: { code: string; number: string; value: string };
  date_of_birth: string;
}

const newRegSchema = yup
  .object()
  .shape({
    email: yup.string().required("Required").email("Enter a valid email"),
    number: yup
      .object({
        code: yup.string().required("Required"),
        number: yup.string().required("Required"),
        value: yup.string().required("Required"),
      })
      .required(),
    first_name: yup.string().required("Required"),
    last_name: yup.string().required("Required"),
    gender: optionTypeSchema().required("Required"),
    date_of_birth: yup.string().required("Required"),
  })
  .required();

interface NewRegistrationFormProps {
  submit: (data: addCandidateAssessmentRequestData) => void;
  assessmentId: string;
}

const NewRegistrationForm: React.FC<NewRegistrationFormProps> = ({
  submit,
  assessmentId,
}) => {
  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
    reset,
    setValue,
  } = useForm<NewRegistrationFormData>({
    resolver: yupResolver(newRegSchema),
    defaultValues: {
      email: "",
      first_name: "",
      last_name: "",
      gender: { label: "", value: "" },
      number: { code: "", number: "", value: "" },
      date_of_birth: "",
    },
  });

  const onSubmit: SubmitHandler<NewRegistrationFormData> = (data) => {
    submit({
      first_name: data.first_name,
      last_name: data.last_name,
      email: data.email,
      gender: data.gender.value,
      phone_number: "0" + data.number.number,
      date_of_birth: data.date_of_birth,
      assessment: assessmentId,
    });
    reset({ number: { code: "234", number: "", value: "" } });
  };

  return (
    <>
      <section className={styles.checkFormRoot}>
        <h2 className={styles.ttl2}>New Candidate Registration</h2>
        <p className={styles.txt}>
          Enter the candidate's details to register them for the assessment.
          Please ensure all fields are filled correctly.
        </p>
        <form>
          <Input
            label="First Name"
            placeholder="e.g. John"
            required
            validatorMessage={errors.first_name?.message}
            name="first_name"
            register={register}
            value={watch("first_name") ?? ""}
            parentClassName={styles.inputWrap}
          />
          <Input
            label="Last Name"
            placeholder="e.g. Doe"
            required
            validatorMessage={errors.last_name?.message}
            name="last_name"
            register={register}
            value={watch("last_name") ?? ""}
            parentClassName={styles.inputWrap}
          />
          <Input
            label="Email address"
            placeholder="e.g. johndoe@gmail.com"
            type="email"
            required
            validatorMessage={errors.email?.message}
            name="email"
            register={register}
            value={watch("email")}
            showRequired
            parentClassName={styles.inputWrap}
          />
          <CustomPhoneInput
            label="Phone number"
            placeholder="Your phone number"
            validatorMessage={errors.number?.value?.message?.toString() ?? ""}
            onChange={({ code, number, value }) =>
              setValue("number", { code, number, value })
            }
            value={watch("number.value")}
            parentClassName={styles.inputWrap}
          />
          <Input
            label="Date of birth"
            placeholder=""
            type="date"
            required
            validatorMessage={errors.date_of_birth?.message}
            name="date_of_birth"
            register={register}
            value={watch("date_of_birth")}
            parentClassName={styles.inputWrap}
          />
          <CustomSelect
            label="Gender"
            placeholder="Select gender"
            options={[
              {
                label: "Male",
                value: "Male",
              },
              {
                label: "Female",
                value: "Female",
              },
            ]}
            onChange={(val) => setValue("gender", val)}
            validatorMessage={errors.gender?.value?.message?.toString() ?? ""}
            name={"gender"}
            value={{
              label: watch("gender")?.label ?? "",
              value: watch("gender")?.value ?? "",
            }}
            parentClassName={styles.inputWrap}
          />

          <Button
            className={styles.btn}
            type={"fill"}
            onClick={handleSubmit(onSubmit)}
          >
            Add candidate
          </Button>
        </form>
      </section>
    </>
  );
};
